<template>
    <Card>
        <Heading
            :labelTitle="$t.__('email.EmailSettingPreferences.title')"
            variant="h2"
            :prefixColoredIcon="{ icon: 'tool', color: 'brand' }"
        />
        <template v-if="isLoading || privateEmailPermissionsLoading">
            <div class="emailSettingPreferences__skeletons">
                <CompassSkeleton
                    class="emailSettingPreferences__skeleton--large"
                    :size="CompassEnums.SkeletonSize.EXTRA_LARGE"
                />
                <CompassSkeleton
                    class="emailSettingPreferences__skeleton--large"
                    :size="CompassEnums.SkeletonSize.EXTRA_LARGE"
                />
                <CompassSkeleton
                    class="emailSettingPreferences__skeleton--medium"
                    :size="CompassEnums.SkeletonSize.EXTRA_LARGE"
                />
                <CompassSkeleton
                    class="emailSettingPreferences__skeleton--small"
                    :size="CompassEnums.SkeletonSize.LARGE"
                />
            </div>
        </template>
        <template v-else>
            <div class="emailSettingPreferences__container emailSettingPreferences__separator">
                <div class="emailSettingPreferences__content">
                    <CompassSwitch
                        :modelValue="localAutomaticallyAttachDocumentValue"
                        :disabled="disableAutomaticallyAttachDocumentSwitch"
                        :label="
                            $t.__('email.EmailSettingPreferences.automaticallyAttachDocument.title')
                        "
                        :caption="
                            $t.__(
                                'email.EmailSettingPreferences.automaticallyAttachDocument.description',
                            )
                        "
                        :orientation="CompassEnums.SwitchOrientation.LEFT"
                        @click="handleAutomaticallyAttachDocumentSwitch"
                    />
                </div>
                <div v-if="privateEmailPermissions" class="emailSettingPreferences__content">
                    <CompassSwitch
                        :modelValue="localPrivateEmailValue"
                        :disabled="disablePrivateEmailSwitch"
                        :label="$t.__('email.EmailSettingPreferences.privateEmails.title')"
                        :caption="$t.__('email.EmailSettingPreferences.privateEmails.description')"
                        :orientation="CompassEnums.SwitchOrientation.LEFT"
                        @click="handlePrivateEmailSwitch"
                    />
                </div>
            </div>

            <div class="emailSettingPreferences__container">
                <div class="emailSettingPreferences__content">
                    <p class="emailSettingPreferences__settingTitle">
                        {{ $t.__('email.EmailSettingPreferences.emailsAddedToCC.title') }}
                    </p>
                    <p class="emailSettingPreferences__settingDescription">
                        {{ $t.__('email.EmailSettingPreferences.emailsAddedToCC.description') }}
                    </p>
                    <EmailSettingPreferencesEmailsInput
                        :modelValue="ccEmails"
                        @update:modelValue="handleCcEmailsUpdate"
                    />
                </div>
            </div>
        </template>
    </Card>
</template>

<script setup lang="ts">
import { ref, computed, inject, watch, onMounted } from 'vue';
import { CompassEnums } from '@sellsy/compass2';
import type { Translator } from 'types/injects/translator.type';
import type { PushToast } from 'types/injects/pushToast.type';
import { useStore } from 'vuex';
import Card from 'vcomponents/common/card/Card.vue';
import Heading from 'vcomponents/common/Heading/Heading.vue';
import EmailSettingPreferencesEmailsInput from 'vcomponents/email/emailSettings/EmailSettingPreferencesEmailsInput.vue';
import StaffApi from 'api/staff/staff';
import { setEmailSettings } from 'api/emailEngine/emailEngineService';

interface Props {
    privateEmail: boolean;
    automaticallyAttachDocumentValue: boolean;
    ccEmails: string[];
    isLoading?: boolean;
}

const props = withDefaults(defineProps<Props>(), {
    isLoading: false,
});

const $t = inject('$t') as Translator;
const $pushToast = inject('$pushToast') as PushToast;

const store = useStore();
const errorsHandler = store.state.system.errorsHandler;
const staffId = store.getters['user/getUser'].id;

const privateEmailPermissions = ref(false);
const privateEmailPermissionsLoading = ref(true);
const localPrivateEmailValue = ref(props.privateEmail);
const disablePrivateEmailSwitch = ref(false);
const localAutomaticallyAttachDocumentValue = ref(props.automaticallyAttachDocumentValue);
const disableAutomaticallyAttachDocumentSwitch = ref(false);
const ccEmails = ref<string[]>(props.ccEmails || []);

const privateEmailSwitchToasterLabel = computed(() => {
    return localPrivateEmailValue.value
        ? $t.__('email.EmailSettingPreferences.privateEmails.isPrivate')
        : $t.__('email.EmailSettingPreferences.privateEmails.isNotPrivate');
});

const getPrivateEmailPermission = () => {
    StaffApi.getStaffPermission(staffId)
        .then((response) => {
            const { _embed } = response.data as { _embed: { acl: Record<string, boolean> } };
            privateEmailPermissions.value = _embed?.acl?.update_email_privacy ?? false;
            privateEmailPermissionsLoading.value = false;
        })
        .catch((error: Error) => {
            errorsHandler.logException(error);
            privateEmailPermissionsLoading.value = false;
        });
};

const handlePrivateEmailSwitch = () => {
    if (disablePrivateEmailSwitch.value) {
        return;
    }
    disablePrivateEmailSwitch.value = true;
    localPrivateEmailValue.value = !localPrivateEmailValue.value;
    setEmailSettings({ private_mode: localPrivateEmailValue.value })
        .then(() => {
            $pushToast({
                type: CompassEnums.ToastType.SUCCESS,
                icon: true,
                close: true,
                text: privateEmailSwitchToasterLabel.value,
            });
        })
        .catch((error: Error) => {
            localPrivateEmailValue.value = !localPrivateEmailValue.value;
            errorsHandler.logException(error);
            $pushToast({
                type: CompassEnums.ToastType.ERROR,
                icon: true,
                close: true,
                text: $t.__('email.EmailSettingPreferences.privateEmails.error'),
            });
        })
        .finally(() => {
            disablePrivateEmailSwitch.value = false;
        });
};

const handleAutomaticallyAttachDocumentSwitch = () => {
    if (disableAutomaticallyAttachDocumentSwitch.value) {
        return;
    }
    disableAutomaticallyAttachDocumentSwitch.value = true;
    localAutomaticallyAttachDocumentValue.value = !localAutomaticallyAttachDocumentValue.value;
    setEmailSettings({ automatically_attach_document: localAutomaticallyAttachDocumentValue.value })
        .then(() => {
            $pushToast({
                type: CompassEnums.ToastType.SUCCESS,
                icon: true,
                close: true,
                text: $t.__('email.EmailSettingPreferences.automaticallyAttachDocument.success'),
            });
        })
        .catch((error: Error) => {
            localAutomaticallyAttachDocumentValue.value =
                !localAutomaticallyAttachDocumentValue.value;
            errorsHandler.logException(error);
            $pushToast({
                type: CompassEnums.ToastType.ERROR,
                icon: true,
                close: true,
                text: $t.__('email.EmailSettingPreferences.automaticallyAttachDocument.error'),
            });
        })
        .finally(() => {
            disableAutomaticallyAttachDocumentSwitch.value = false;
        });
};

const handleCcEmailsUpdate = (emails: string[]) => {
    setEmailSettings({ cc_emails: emails })
        .then(() => {
            ccEmails.value = emails;
            $pushToast({
                type: CompassEnums.ToastType.SUCCESS,
                icon: true,
                close: true,
                text: $t.__('email.EmailSettingPreferences.emailsAddedToCC.success'),
            });
        })
        .catch((error: Error) => {
            errorsHandler.logException(error);
            $pushToast({
                type: CompassEnums.ToastType.ERROR,
                icon: true,
                close: true,
                text: $t.__('email.EmailSettingPreferences.emailsAddedToCC.error'),
            });
        });
};

watch(
    () => [props.privateEmail, props.automaticallyAttachDocumentValue, props.ccEmails] as const,
    ([newPrivateEmail, newAttachDocument, newCcEmails]) => {
        localPrivateEmailValue.value = newPrivateEmail;
        localAutomaticallyAttachDocumentValue.value = newAttachDocument;
        ccEmails.value = newCcEmails || [];
    },
);

onMounted(() => {
    getPrivateEmailPermission();
});
</script>

<style lang="scss" scoped>
.emailSettingPreferences {
    p {
        margin: 0;
    }

    &__title {
        margin: 0 0 var(--spacing-1);
    }

    &__skeletons {
        display: flex;
        flex-direction: column;
        gap: var(--spacing-3);
    }

    &__skeleton--large {
        max-width: 652px;
    }

    &__skeleton--medium {
        max-width: 372px;
    }

    &__skeleton--small {
        max-width: 140px;
    }

    &__container {
        display: flex;
        flex-direction: column;
        gap: var(--spacing-3);
    }

    &__separator {
        border-bottom: 1px solid var(--colors-common-border-default);
        padding-bottom: var(--spacing-3);
        margin-bottom: var(--spacing-3);
    }

    &__content {
        display: block;
        width: 100%;

        :deep(.compass-switch label) {
            justify-content: start;
        }
    }

    &__description {
        color: var(--colors-common-text-subtle-default);
    }

    &__settingTitle {
        font-size: var(--font-size-14);
        line-height: var(--line-heights-20);
        margin: 0 0 var(--spacing-0) 0;
    }

    &__settingDescription {
        font-size: var(--font-size-12);
        line-height: var(--line-heights-16);
        color: var(--colors-common-text-subtle-default);
        margin: 0 0 var(--spacing-1) 0;
    }
}
</style>
